import { Grid, IconButton, ToggleButton, ToggleButtonGroup, Tooltip, useTheme } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';

import React, { useRef, useState } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import './Orders.scss';
import { CalendarToday, Delete, Edit, Schedule, Update } from '@mui/icons-material';
import Typography from '@mui/material/Typography';
import TimeUtils from '../utils/TimeUtils';
import './UpcomingOrders.scss';
import { useCalendar } from '../calendar/CalendarContext/CalendarContext';
import { UseAuthPermissions } from '../session/UseAuthPermissions';
import { getExternalVoucherStatusChip, isExternalVoucherUnapproved } from '../externalVouchers/utils';
import { AccountData, OrderData, OrderPageData, OrderService, OrderSortBy, OrderStatus, RefundOwnerType } from '../api';
import { WorkshopCircle } from '../common/ui/WorkshopCircle';
import { useAuth } from '../session/InternalAuthProvider';
import { useLanguage } from '../common/GeneralUtils';
import ConfirmationDialog from '../common/ui/ConfimationDialog';
import { RefundOfferDialog, RefundOfferTarget } from '../refunds/RefundOfferDialog';
import EditOrderDialog from '../calendar/dialogs/EditOrderDialog/EditOrderDialog';
import { CellProps, useTable } from '../table/useTable';
import { getDateFormat, getTimeFormat } from '../common/getDateAndTimeFormat';
import { CalendarView, DATE_FORMAT } from '../calendar/calendarWrapper/CalendarWrapper';
import { TABLE_PHONE_ID, TABLE_PRICE_ID } from '../table/TableComponent';
import { getOrderPages, isRescheduleEnabledForOrderPage } from '../utils/AccountUtils';
import { ProjectSelectionPendingBadge } from '../common/ProjectSelectionPendingBadge';
import PickupNotificationButton from '../calendar/dialogs/PickupNotificationButton';
import CopyToClipboardButton from '../calendar/dialogs/CopyToClipboardButton';
import { OrdersFilter } from './OrdersFilter/OrdersFilter';
import { ALL_ORDER_STATUSES, useOrdersFilter } from './OrdersFilter/OrdersFilterContext';

type OrdersTableProps = {
  fetchingMethod: (
    page?: number,
    perPage?: number,
    search?: string,
    sortBy?: OrderSortBy,
    orderPageIds?: string[],
    statuses?: OrderStatus[],
  ) => Promise<OrderData[]>;
  opId: string;
  hideActions?: boolean;
  withArchivedColumns?: boolean;
};
const TABULAR_SX = { fontVariantNumeric: 'tabular-nums' } as const;

export const OrdersTable = ({ fetchingMethod, opId, hideActions, withArchivedColumns }: OrdersTableProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;

  const language = useLanguage();
  const [selectedOrder, setSelectedOrder] = useState<OrderData | undefined>();
  const [deleteOrderIsOpen, setDeleteOrderIsOpen] = useState(false);
  const [refundOffer, setRefundOffer] = useState<RefundOfferTarget | undefined>(undefined);
  const [sortBy, setSortBy] = useState<OrderSortBy>(OrderSortBy.CREATED_AT);
  const { selectedOrderPageIds, statuses } = useOrdersFilter();
  const mutateRef = useRef<((data?: OrderData[]) => void) | undefined>();
  const dataRef = useRef<OrderData[] | undefined>();
  const { readOnly, isAtLeastSupervisor } = UseAuthPermissions();
  const navigate = useNavigate();
  const timeFormat = getTimeFormat(account);

  const statusesForFetch = withArchivedColumns || statuses.length === ALL_ORDER_STATUSES.length ? undefined : statuses;

  const { shortDateFormat, longDateFormat } = getDateFormat(account);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const fetcher = async ({ search, page, perPage }: { search?: string; page: number; perPage: number }) =>
    fetchingMethod(
      page,
      perPage,
      search,
      sortBy,
      selectedOrderPageIds?.length ? selectedOrderPageIds : undefined,
      statusesForFetch,
    );

  const { openEditOrderDrawer, editOrderDrawerState, closeEditOrderDrawer, users } = useCalendar();

  function formatCreatedAt(created_at: string) {
    const createdAt = TimeUtils.getLocalTime(created_at, account.timezone);
    const isToday = createdAt.isSame(moment(), 'day');
    return (
      <Typography
        variant={isToday ? 'body1' : 'body2'}
        color={isToday ? 'primary.main' : undefined}
        fontWeight={isToday ? 700 : undefined}
        dir="ltr"
        sx={TABULAR_SX}>
        {createdAt.format(isToday ? timeFormat : shortDateFormat)}
      </Typography>
    );
  }

  const archivedColumns: CellProps<OrderData>[] = [
    {
      id: 'archived_by',
      alignment: 'center',
      label: t('calendar.edit_order.archived_by'),
      hideCell: !withArchivedColumns,
      render: (order: OrderData) => {
        const archivedByUser = users?.find((u) => u.user_id === order.archived_by_user_id);
        return archivedByUser ? `${archivedByUser.first_name} ${archivedByUser.last_name}` : order.archived_by_user_id;
      },
    },
    {
      id: 'archive_timestamp',
      alignment: 'center',
      label: t('calendar.edit_order.archived_at'),
      hideCell: !withArchivedColumns,
      render: (order: OrderData) =>
        order.archive_timestamp && moment(order.archive_timestamp).format(`${longDateFormat}`),
    },
  ];
  const tableDetails: CellProps<OrderData>[] = [
    {
      id: 'name',
      alignment: 'center',
      label: t('orders.name'),
      render: (order: OrderData) => (
        <Grid container gap={0.5} alignItems="center" flexWrap="nowrap" justifyContent="flex-start">
          <ProjectSelectionPendingBadge order={order} account={account} />
          {order.pass_id ? (
            <Link
              to={`/dashboard/passes?search=${encodeURIComponent(`${order.firstname} ${order.lastname}`)}`}
              style={{ color: 'inherit', textDecoration: 'underline' }}>
              {`${order.firstname} ${order.lastname}`}
            </Link>
          ) : (
            `${order.firstname} ${order.lastname}`
          )}
        </Grid>
      ),
    },
    {
      id: 'createdAt',
      alignment: 'center',
      label: (
        <Grid
          container
          alignItems="center"
          sx={{ cursor: 'pointer' }}
          onClick={() => setSortBy(OrderSortBy.CREATED_AT)}>
          <IconButton size="small">
            <Schedule sx={{ fontSize: 18, color: sortBy === OrderSortBy.CREATED_AT ? 'primary.main' : undefined }} />
          </IconButton>
          <Typography
            sx={{
              fontSize: 'inherit',
              fontWeight: sortBy === OrderSortBy.CREATED_AT ? 600 : 'inherit',
              color: sortBy === OrderSortBy.CREATED_AT ? 'primary.main' : undefined,
            }}>
            {t('orders.date')}
          </Typography>
        </Grid>
      ),
      render: (order: OrderData) => formatCreatedAt(order.created_at),
    },
    {
      id: 'numPersons',
      alignment: 'center',
      label: t('orders.num_persons'),
      render: (order: OrderData) => (
        <Typography variant="body2" dir="ltr" sx={TABULAR_SX}>
          {order.persons}
        </Typography>
      ),
    },
    {
      id: 'orderDate',
      alignment: 'center',
      label: (
        <Grid container alignItems="center" sx={{ cursor: 'pointer' }} onClick={() => setSortBy(OrderSortBy.DATE)}>
          <IconButton size="small">
            <CalendarToday sx={{ fontSize: 18, color: sortBy === OrderSortBy.DATE ? 'primary.main' : undefined }} />
          </IconButton>
          <Typography
            sx={{
              fontSize: 'inherit',
              fontWeight: sortBy === OrderSortBy.DATE ? 600 : 'inherit',
              color: sortBy === OrderSortBy.DATE ? 'primary.main' : undefined,
            }}>
            {t('order_date')}
          </Typography>
        </Grid>
      ),
      render: (order: OrderData) => {
        const momentDate = moment(order.date);
        const calendarView = localStorage.getItem('webook-calendarview');
        const startOfUnit =
          calendarView === CalendarView.THREE_DAY ||
          calendarView === CalendarView.AREAS ||
          calendarView === CalendarView.DAY
            ? 'day'
            : calendarView;
        // @ts-ignore
        const weekStart = momentDate.clone().startOf(startOfUnit);
        return (
          <Link
            to={`/dashboard/calendar/${weekStart.format(DATE_FORMAT)}?entityid=${order.parent_id || order.id}`}
            dir="ltr"
            style={{ fontVariantNumeric: 'tabular-nums' }}>
            {momentDate.format(shortDateFormat)}
          </Link>
        );
      },
    },
    {
      id: TABLE_PHONE_ID,
      alignment: 'center',
      width: 140,
      label: t('orders.phone'),
    },
    {
      id: 'email',
      alignment: 'center',
      label: t('orders.email'),
      render: (order: OrderData) => (
        <Grid container gap={1} alignItems={'center'}>
          <CopyToClipboardButton text={order.email} width={'18px'} color={theme.palette.text.secondary} />
          <Typography style={{ textTransform: 'none' }}>{order.email}</Typography>
        </Grid>
      ),
    },
    {
      id: 'workshop',
      alignment: 'center',
      label: t('orders.workshop'),
      render: (order: OrderData) => {
        const orderPage = getOrderPages(account).find((x: OrderPageData) => x.id === order.order_page_id);
        return (
          <Grid container gap={1} flexWrap="nowrap" alignItems={'center'}>
            <WorkshopCircle account={account} orderPageId={order.order_page_id} language={language} withTooltip />
            <Typography
              variant={'body2'}
              sx={{
                maxWidth: 100,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
              {orderPage?.label[language]}
            </Typography>
          </Grid>
        );
      },
    },
    {
      id: TABLE_PRICE_ID,
      alignment: 'center',
      label: t('orders.price'),
      width: 100,
    },
    {
      id: 'orderNumber',
      alignment: 'center',
      label: t('order_number'),
      render: (order: OrderData) => (
        <Typography variant="body2" dir="ltr" sx={TABULAR_SX}>
          {`#${order.order_number}`}
        </Typography>
      ),
    },
    {
      id: 'duration',
      alignment: 'center',
      label: t('orders.duration'),
      render: (order: OrderData) => TimeUtils.formatDuration(order.duration, t),
    },
    {
      id: 'status',
      alignment: 'center',
      label: t('orders.status'),
      // External voucher orders carry a second, independent state: where the code is in
      // reconciliation. Without it a booking awaiting approval is indistinguishable from a settled one.
      render: (order: OrderData) => (
        <Grid container direction="column" alignItems="flex-start" gap={0.5}>
          <Typography variant="body2">{t(`order_status.${order.status.toLowerCase()}`)}</Typography>
          {isExternalVoucherUnapproved(order) &&
            getExternalVoucherStatusChip(order.external_voucher_redemption?.status, t, () =>
              navigate(`/dashboard/external-vouchers?status=${order.external_voucher_redemption?.status}`),
            )}
        </Grid>
      ),
    },
    {
      id: 'update',
      alignment: 'left',
      hideCell: hideActions,
      width: 30,
      noPadding: true,
      render: (order: OrderData) => (
        <Tooltip
          title={
            !isAtLeastSupervisor
              ? t('noPermissions')
              : isRescheduleEnabledForOrderPage(account, order.order_page_id)
              ? t('amend')
              : t('amend_disabled')
          }
          arrow>
          <span>
            <CopyToClipboardButton
              IconComponent={Update}
              text={`${import.meta.env.VITE_REACT_APP_APP_DOMAIN}/manage-booking?orderid=${order.id}`}
              orderPageId={order.order_page_id}
              disabled={
                readOnly ||
                !isRescheduleEnabledForOrderPage(account, order.order_page_id) ||
                order.status === OrderStatus.CONVERTED_TO_VOUCHER
              }
            />
          </span>
        </Tooltip>
      ),
    },
  ];

  if (withArchivedColumns) {
    tableDetails.push(...archivedColumns);
  }
  tableDetails.push({
    id: 'pickup',
    alignment: 'left',
    hideCell: hideActions,
    width: 30,
    noPadding: true,
    render: (order: OrderData) => (
      <Grid item sx={{ px: 1 }}>
        <PickupNotificationButton
          order={order}
          account={account}
          compact
          onOrderUpdate={(updatedOrder: OrderData) => {
            const orders = (dataRef.current || []).map((x) => (x.id === updatedOrder.id ? updatedOrder : x));
            mutateRef.current?.(orders);
            return Promise.resolve();
          }}
        />
      </Grid>
    ),
  });
  tableDetails.push({
    id: 'delete',
    alignment: 'left',
    hideCell: hideActions,
    width: 30,
    noPadding: true,
    render: (order: OrderData) => (
      <Tooltip title={readOnly ? t('noPermissions') : t('delete')}>
        <span>
          <IconButton
            disabled={readOnly || order.status === OrderStatus.CONVERTED_TO_VOUCHER}
            className="action-icon"
            onClick={() => {
              setSelectedOrder(order);
              setDeleteOrderIsOpen(true);
            }}>
            <Delete />
          </IconButton>
        </span>
      </Tooltip>
    ),
  });
  tableDetails.unshift({
    id: 'edit',
    alignment: 'left',
    width: 30,
    noPadding: true,
    render: (order: OrderData) => (
      <Tooltip title={!isAtLeastSupervisor ? t('noPermissions') : t('edit')}>
        <span>
          <IconButton
            disabled={!isAtLeastSupervisor || order.status === OrderStatus.CONVERTED_TO_VOUCHER}
            className="action-icon"
            onClick={() => {
              openEditOrderDrawer({ order, orderPageId: order.order_page_id, parentId: order.parent_id });
            }}>
            <Edit />
          </IconButton>
        </span>
      </Tooltip>
    ),
  });
  const { mutate, data, TableComponent } = useTable<OrderData>({
    opId: `${opId}-${sortBy}-${[...(selectedOrderPageIds ?? [])].sort().join(',')}-${[...(statusesForFetch ?? [])]
      .sort()
      .join(',')}`,
    fetcher,
    tableDetails,
    fillHeight: true,
    actionsComponent: (
      <Grid container alignItems="center" gap={1}>
        <OrdersFilter hideStatuses={withArchivedColumns} />
        <ToggleButtonGroup value={sortBy} exclusive onChange={(_, value) => value && setSortBy(value)} size="small">
          <ToggleButton value={OrderSortBy.DATE}>
            <Tooltip title={t('orders.sortByDate')}>
              <CalendarToday />
            </Tooltip>
          </ToggleButton>
          <ToggleButton value={OrderSortBy.CREATED_AT}>
            <Tooltip title={t('orders.sortByCreated')}>
              <Schedule />
            </Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>
      </Grid>
    ),
  });
  mutateRef.current = mutate;
  dataRef.current = data;
  return (
    <Grid container flexDirection="column" flexWrap="nowrap" sx={{ flex: 1, minHeight: 0 }}>
      {TableComponent}
      <EditOrderDialog
        editOrderDialogState={editOrderDrawerState}
        closeEditOrderDialog={closeEditOrderDrawer}
        updateOrder={(orderToUpdate: OrderData) => {
          const orders = data?.filter((x) => x.id !== selectedOrder?.id) || [];
          void mutate([...orders, orderToUpdate]);
        }}
      />
      <ConfirmationDialog
        title={t('calendar.delete_order.title')}
        content={<Typography>{t('calendar.delete_order.description')}</Typography>}
        isOpen={deleteOrderIsOpen}
        onClose={async (actionConfirmed) => {
          if (actionConfirmed && selectedOrder) {
            await OrderService.deleteOrder(selectedOrder.id);
            const orders = data?.filter((x) => x.id !== selectedOrder.id) || [];
            void mutate(orders);
            setRefundOffer({
              ownerType: RefundOwnerType.ORDER,
              ownerId: selectedOrder.id,
              title: t('refunds.offer.orderTitle'),
              customerName: `${selectedOrder.firstname} ${selectedOrder.lastname}`,
              ownerLabel: t('auditlog.entity_info.order', { orderNumber: selectedOrder.order_number }),
            });
          }
          setDeleteOrderIsOpen(false);
        }}
      />
      {refundOffer && <RefundOfferDialog isOpen onClose={() => setRefundOffer(undefined)} {...refundOffer} />}
    </Grid>
  );
};
